<template>
	<div class="teaser-horizontal">
		<p class="episode-number vl-font-size--50" v-if="width >= 835">{{ number }}</p>
		<div class="image">
			<div
				v-if="timeline"
				class="video-timeline-progress"
				:style="{
					background: `linear-gradient(to right, #f13c36 0%, #f13c36 ${timeline}%, #ffffff ${timeline}%, #ffffff 100%)`,
				}"
			></div>
			<img :src="episode.field_image?.image_style_uri?.episode" :alt="episode.field_image?.meta?.alt" />
		</div>
		<div class="teaser-medium--description">
			<div class="section--top">
				<div class="section--top-left">
					<p class="episode-number vl-font-size--50" v-if="width < 835">{{ number }}</p>
					<p class="vl-font-size--22 title">{{ episode.title }}</p>
				</div>
				<p class="vl-font-size--22 duration">{{ episode.field_duration }}</p>
			</div>
			<p class="vl-font-size--16" v-html="episode?.field_description?.processed"></p>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { computed, onMounted, ref } from "vue"
	import { Episode } from "../../types/Episode"
	import { useUserProfiles } from "../../composables/useUserProfile"
	import { useTimestampStore } from "../../stores/timestamps"
	import { extractDurationFromKwik } from "../../utils/media"
	import { useWindowSize } from "@vueuse/core"
	const timestampStore = useTimestampStore()
	const { width, height } = useWindowSize()

	const videoUrl = ref<null | string>(null)
	const timeline = ref(0)
	const duration = ref(0)
	const time = ref(0)
	const { fetchVideoFlagStoreByProfile } = useUserProfiles()
	const props = defineProps<{
		number: number
		episode: Episode
	}>()
	onMounted(async () => {
		const seriesId = (props.episode as Episode)?.field_series?.id
		const res = ref([])
		res.value = await fetchVideoFlagStoreByProfile(props.episode.id, "episode", "started", seriesId)
		if (!(res.value.length > 0)) {
			res.value = await fetchVideoFlagStoreByProfile(props.episode.id, "episode", "completed", seriesId)
		}
		const first = res.value[0]
		if (first) {
			const epTs = first?.timestamp
			if (typeof epTs === "number") {
				timestampStore.addTimestamp(epTs, "node--episode", props.episode.drupal_internal__nid)
			}
			time.value = epTs || 0
		} else {
			time.value = 0
		}
		duration.value = extractDurationFromKwik((props.episode as Episode)?.field_kwik_data) ?? 0
		timeline.value = (time.value / duration.value) * 100
	})
	videoUrl.value = props.episode.field_episode?.uri || null
</script>

<style scoped lang="scss">
	.teaser-horizontal {
		display: flex;
		gap: 20px;
		align-items: center;
		padding: 60px 0;
		border-bottom: 1px solid var(--gray);
		&:first-child {
			padding-top: 30px;
		}
		&:last-child {
			padding-bottom: 0;
			border-bottom: 0px;
		}
		&:hover {
			cursor: pointer;
			img {
				transform: scale(1.05);
			}
			.image::after {
				opacity: 1;
			}
		}
		@include br.respond-to(br.$tablet-large) {
			flex-wrap: wrap;
			justify-content: center;
		}
	}
	p {
		margin: 0;
	}
	.video-timeline-progress {
		position: absolute;
		bottom: 0px;
		height: 6px;
		left: 0;
		right: 0;
		z-index: 2;
		transition: all 0.3s ease;
	}
	.episode-number {
		min-width: 56px;
		@include br.respond-to(br.$tablet-large) {
			min-width: unset;
		}
	}
	.section--top {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 15px;
		margin-bottom: 20px;
		&-left {
			display: flex;
			align-items: center;
			gap: 15px;
		}
	}
	.image {
		transition: all 0.3s ease;
		max-width: 277px;
		width: 100%;
		flex-shrink: 0;
		border-radius: 10px;
		overflow: hidden;
		position: relative;
		z-index: 0;
		&::after {
			content: "";
			position: absolute;
			transition: all 0.3s ease;
			left: 0;
			top: 0;
			right: 0;
			bottom: 0;
			z-index: 1;
			background-color: rgba($color: #000000, $alpha: 0.5);
			background-image: url("../../images/play-circle.svg");
			background-repeat: no-repeat;
			background-position: center;
			opacity: 0;
		}
		@include br.respond-to(br.$tablet-small) {
			max-width: unset;
		}
	}
	img {
		width: 100%;
		display: block;
		transition: all 0.3s ease;
		max-height: 145px;
		object-fit: cover;

		@include br.respond-to(br.$tablet-small) {
			max-height: 220px;
		}
	}

	.cutoff-text {
		--max-line: 3;
		display: -webkit-box;
		overflow: hidden;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: var(--max-line);
	}
</style>
